import { format } from "date-fns";
import {
  FileText,
  User,
  CreditCard,
  Stethoscope,
  Accessibility,
  Heart,
  Camera,
  ShieldCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface PatientIntakeFormProps {
  patient: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    ssn_last_four: string | null;
    phone: string;
    email: string | null;
    address_line1: string;
    address_line2: string | null;
    city: string;
    state: string;
    zip_code: string;
    insurance_type: string;
    medicare_id: string | null;
    medicaid_id: string | null;
    medicare_part_a: boolean | null;
    medicare_part_b: boolean | null;
    medicare_part_c: boolean | null;
    medicare_part_d: boolean | null;
    primary_care_physician: string | null;
    pcp_phone: string | null;
    emergency_contact_name: string;
    emergency_contact_phone: string;
    emergency_contact_relationship: string;
    current_medications: string | null;
    allergies: string | null;
    chronic_conditions: string | null;
    mobility_aids: string[] | null;
    hearing_devices: string[] | null;
    vision_devices: string[] | null;
    communication_devices: string[] | null;
    other_assistive_devices: string | null;
    accessibility_needs: string | null;
    hobbies: string[] | null;
    social_activities: string[] | null;
    living_situation: string | null;
    social_support_level: string | null;
    preferred_communication_style: string | null;
    interests_notes: string | null;
    patient_photo_url: string | null;
    insurance_card_front_url: string | null;
    insurance_card_back_url: string | null;
    consent_given: boolean;
    consent_date: string | null;
    signature: string | null;
    status: string | null;
    created_at: string;
  };
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium">{value || "—"}</p>
    </div>
  );
}

function TagList({ label, items }: { label: string; items: string[] | null | undefined }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((item) => (
          <Badge key={item} variant="secondary" className="text-xs">
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );
}

export function PatientIntakeForm({ patient }: PatientIntakeFormProps) {
  const medicareParts = [
    patient.medicare_part_a && "Part A",
    patient.medicare_part_b && "Part B",
    patient.medicare_part_c && "Part C",
    patient.medicare_part_d && "Part D",
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold">Patient Intake Form</p>
                <p className="text-sm text-muted-foreground">
                  Submitted {patient.created_at ? format(new Date(patient.created_at), "MMMM d, yyyy 'at' h:mm a") : "—"}
                </p>
              </div>
            </div>
            <Badge variant={patient.status === "completed" ? "default" : patient.status === "pending" ? "secondary" : "outline"}>
              {patient.status || "Unknown"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <InfoRow label="First Name" value={patient.first_name} />
              <InfoRow label="Last Name" value={patient.last_name} />
            </div>
            <InfoRow label="Date of Birth" value={patient.date_of_birth ? format(new Date(patient.date_of_birth), "MMMM d, yyyy") : null} />
            {patient.ssn_last_four && <InfoRow label="SSN (Last 4)" value={`***-**-${patient.ssn_last_four}`} />}
            <InfoRow label="Phone" value={patient.phone} />
            <InfoRow label="Email" value={patient.email} />
            <Separator />
            <InfoRow label="Address" value={`${patient.address_line1}${patient.address_line2 ? `, ${patient.address_line2}` : ""}`} />
            <InfoRow label="City, State, ZIP" value={`${patient.city}, ${patient.state} ${patient.zip_code}`} />
          </CardContent>
        </Card>

        {/* Insurance Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4" />
              Insurance Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Insurance Type</p>
              <Badge variant="outline" className="capitalize mt-1">
                {patient.insurance_type?.replace("_", " ")}
              </Badge>
            </div>
            {patient.medicare_id && <InfoRow label="Medicare ID" value={patient.medicare_id} />}
            {patient.medicaid_id && <InfoRow label="Medicaid ID" value={patient.medicaid_id} />}
            {medicareParts.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Medicare Coverage</p>
                <div className="flex gap-1">
                  {medicareParts.map((part) => (
                    <Badge key={part as string} variant="secondary" className="text-xs">{part as string}</Badge>
                  ))}
                </div>
              </div>
            )}
            <Separator />
            <InfoRow label="Primary Care Physician" value={patient.primary_care_physician} />
            <InfoRow label="PCP Phone" value={patient.pcp_phone} />
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              Emergency Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label="Name" value={patient.emergency_contact_name} />
            <InfoRow label="Relationship" value={patient.emergency_contact_relationship} />
            <InfoRow label="Phone" value={patient.emergency_contact_phone} />
          </CardContent>
        </Card>

        {/* Medical History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Stethoscope className="h-4 w-4" />
              Medical History
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label="Current Medications" value={patient.current_medications} />
            <InfoRow label="Allergies" value={patient.allergies} />
            <InfoRow label="Chronic Conditions" value={patient.chronic_conditions} />
          </CardContent>
        </Card>

        {/* Accessibility */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Accessibility className="h-4 w-4" />
              Accessibility & Devices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <TagList label="Mobility Aids" items={patient.mobility_aids} />
            <TagList label="Hearing Devices" items={patient.hearing_devices} />
            <TagList label="Vision Devices" items={patient.vision_devices} />
            <TagList label="Communication Devices" items={patient.communication_devices} />
            <InfoRow label="Other Assistive Devices" value={patient.other_assistive_devices} />
            <InfoRow label="Accessibility Needs" value={patient.accessibility_needs} />
          </CardContent>
        </Card>

        {/* Social & Lifestyle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Heart className="h-4 w-4" />
              Social & Lifestyle
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <TagList label="Hobbies" items={patient.hobbies} />
            <TagList label="Social Activities" items={patient.social_activities} />
            <InfoRow label="Living Situation" value={patient.living_situation} />
            <InfoRow label="Social Support Level" value={patient.social_support_level} />
            <InfoRow label="Preferred Communication" value={patient.preferred_communication_style} />
            <InfoRow label="Notes" value={patient.interests_notes} />
          </CardContent>
        </Card>

        {/* Photos & Documents */}
        {(patient.patient_photo_url || patient.insurance_card_front_url || patient.insurance_card_back_url) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Camera className="h-4 w-4" />
                Photos & Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {patient.patient_photo_url && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Patient Photo</p>
                  <img src={patient.patient_photo_url} alt="Patient" className="w-24 h-24 rounded-lg object-cover" />
                </div>
              )}
              <div className="flex gap-4">
                {patient.insurance_card_front_url && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Insurance Card (Front)</p>
                    <img src={patient.insurance_card_front_url} alt="Insurance front" className="w-40 h-24 rounded-lg object-cover border" />
                  </div>
                )}
                {patient.insurance_card_back_url && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Insurance Card (Back)</p>
                    <img src={patient.insurance_card_back_url} alt="Insurance back" className="w-40 h-24 rounded-lg object-cover border" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consent */}
        <Card className={!(patient.patient_photo_url || patient.insurance_card_front_url || patient.insurance_card_back_url) ? "md:col-span-2" : ""}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4" />
              Consent & Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">Consent Given:</p>
              <Badge variant={patient.consent_given ? "default" : "destructive"}>
                {patient.consent_given ? "Yes" : "No"}
              </Badge>
            </div>
            {patient.consent_date && (
              <InfoRow label="Consent Date" value={format(new Date(patient.consent_date), "MMMM d, yyyy 'at' h:mm a")} />
            )}
            {patient.signature && <InfoRow label="Signature" value={patient.signature} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
